// Release notes for the What's New page (/whats-new), newest first. Hand-edited
// from the desktop app's history (MikeShirley/Rally-Navigator-2---Desktop), with
// a headline and optional guide `link` per item.
//
// Newer versions don't have to be added here: the build fetches the app's own
// release notes from the download server (scripts/fetch-release-notes.js), and
// any version newer than the top entry below is shown from those automatically.
// Add an entry here only to give a release a headline and guide links.
import appVersion from '../generated/app-version.json';
import previousVersion from '../generated/previous-version.json';
import releaseNotes from '../generated/release-notes.json';

export const RELEASES = [
  {
    version: '2.6.8',
    date: '2026-10-05',
    headline: 'Sharper OpenRally Notes',
    items: [
      { text: 'The Notes column in OpenRally exports is sharp again — it had been exported at reduced resolution.', link: '/timing-scoring' },
      { text: 'Copy, cut and paste of trackpoints works again after clicking on the map.' },
    ],
  },
  {
    version: '2.6.7',
    date: '2026-09-28',
    headline: 'Distances That Always Add Up',
    items: [
      { text: 'Partial distances now always match the printed totals — on screen, in the PDF, on the TSD sheet and in the OpenRally GPX.', link: '/time-speed-distance' },
      { text: 'Restart App finishes installing an update on Mac and Windows without a reboot, and asks about unsaved changes first.' },
    ],
  },
  {
    version: '2.6.6',
    date: '2026-09-27',
    headline: 'Road Rally Time Control Upgrades',
    items: [
      { text: 'New Remote Service zone in the Time Control dialog.', link: '/how-to-create-a-road-rally' },
      { text: 'Road Rally: each time control prints its own number (TC1A, TC1B…) instead of the section number.', link: '/how-to-create-a-road-rally' },
      { text: 'Control durations are typed as hours and minutes (h:mm), and dialogs stay where you drag them.' },
      { text: 'Icons from your rallynavigator.com account appear in printed PDFs again, and PDF printing no longer freezes on certain custom icon images.' },
      { text: 'Changing the base map keeps your zoom, and the map and elevation profile fill their pane again after resizing.' },
    ],
  },
  {
    version: '2.6.5',
    date: '2026-09-25',
    headline: 'Converted Roadbooks Export Correctly',
    items: [
      { text: 'A roadbook converted from Road Rally to Cross Country now exports PDF and GPX normally.' },
    ],
  },
  {
    version: '2.6.4',
    date: '2026-09-24',
    headline: 'Change Roadbook Format',
    items: [
      { text: 'Convert a saved roadbook between Cross Country, Road Rally and FIA Dakar formats — the first item in the Roadbook menu.' },
    ],
  },
  {
    version: '2.6.3',
    date: '2026-09-24',
    headline: 'Smoother Map, Smarter Track Cleanup',
    items: [
      { text: 'Remove Extra Track Points previews the result before you run it — points now, points after, and the reduction — then reports what it removed.' },
      { text: 'New sign post icon in the Landmarks icons.', link: '/rally-tulip-diagrams' },
      { text: 'The map opens at the zoom you left it at and keeps your zoom as you click between waypoints.' },
      { text: 'Sharper Google satellite imagery at full zoom, and smoother zooming on large roadbooks.' },
    ],
  },
  {
    version: '2.6.2',
    date: '2026-09-23',
    headline: 'Opening and Import Fixes',
    items: [
      { text: 'Windows: roadbooks stored on another drive, outside your user folder or on a network drive open again.' },
      { text: 'KMZ files import again, and KMZ map overlays draw again.' },
      { text: 'Mac: opening a roadbook from Finder or after closing another brings the window to the front.' },
    ],
  },
  {
    version: '2.6.1',
    date: '2026-09-05',
    headline: 'Faster With Big GPS Tracks',
    items: [
      { text: 'Large GPS tracks are simplified automatically on GPX/KML/KMZ import, and very dense roadbooks offer to remove extra points so the app stays fast.', link: '/how-to-create-a-cross-country' },
      { text: 'Remove Extra Track Points is dramatically faster — a 22,000-point roadbook went from nearly two minutes to under two seconds. Reverse Track is over 3x faster.' },
      { text: 'KMZ export groups waypoints into folders by control type in Google Earth. (Pro)', link: '/pro-features' },
      { text: 'GPX export profiles for Stella, Stella Anube, RallySafe, RacingTrax, Sporttrax, TeamTrack, VDS Racing and YB Tracking.', link: '/timing-scoring' },
      { text: 'Four Estonian Land Board map sources — Map, Hybrid, Grey Map and Orthophoto — beside the IGN France and Spain maps. (Pro)', link: '/pro-features' },
      { text: 'Crash recovery: a background copy of your roadbook is offered for restore if the app ever closes unexpectedly. Plus a major platform update for performance and security.' },
    ],
  },
  {
    version: '2.5.2 – 2.5.3',
    date: '2026-08-17',
    headline: 'GPS Recorder, Tulip Assist and More',
    items: [
      { text: 'GPS Roadbook Recorder: drive the route and create the roadbook live. GPS Roadbook Validator: re-drive a roadbook to check waypoints and add new ones. (Pro)', link: '/pro-features' },
      { text: 'Tulip Assist drafts a tulip’s roads and no-go lines from the map for you to review and approve. (Pro)', link: '/pro-features' },
      { text: 'More map sources, hover-over track distance, QR code icons and KMZ export for Google Earth. (Pro)', link: '/pro-features' },
      { text: 'Import RiverNotes 2 (.tvrx/.tprj) and Tulip (.tlp) roadbooks, and bring GPX/KML/KMZ points in as waypoints.' },
      { text: 'Faster editing: box selection, Follow Roads mid-route, spacebar to toggle waypoint/trackpoint, a map scale bar and coordinate search.' },
      { text: 'FIA Dakar layout updated to the FIA spec (MODIF / NEW NOTE marker), plus Bulgarian and Chinese (Simplified) app languages.', link: '/fim-fia-roadbook-specs-rules' },
    ],
  },
  {
    version: '2.5.1',
    date: '2026-04-06',
    headline: 'Dakar Layout for Everyone',
    items: [
      { text: 'The FIA Dakar layout is available to all users.', link: '/fim-fia-roadbook-specs-rules' },
      { text: 'Polish, Russian and Japanese app languages.' },
      { text: 'Road Rally roadbooks export to OpenRally and Terrapirata formats.', link: '/timing-scoring' },
      { text: 'New petrol station icon for Cross Country and Road Rally, login that stays signed in, and update downloads with a progress bar.' },
      { text: 'Fixed TSD export when the file was already open, and roadbook title and description saving.', link: '/time-speed-distance' },
    ],
  },
  {
    version: '2.5.0',
    date: '2025-11-20',
    headline: 'Elevation Profile and a New Look',
    items: [
      { text: 'Major interface update with a redesigned dark theme and on-screen notifications.' },
      { text: 'FIA Dakar layout: wider tulip box, narrower notes box.', link: '/fim-fia-roadbook-specs-rules' },
      { text: 'Elevation profile on screen and in the PDF, plus an elevation smart icon. (Now Pro)', link: '/pro-features' },
      { text: 'Add a Google Street View snapshot to the notes box background. (Pro)', link: '/pro-features' },
      { text: 'Map overlays with multiple tracks, show/hide and track colors; colour-coded track sections (SS, DZ, NEU, Liaison).', link: '/rally-maps' },
      { text: 'More control icons for Cross Country and Road Rally, and richer OpenRally exports.', link: '/timing-scoring' },
    ],
  },
  {
    version: '2.4.6',
    date: '2025-10-22',
    headline: 'Custom Icon Tabs and KMZ Overlays',
    items: [
      { text: 'Multiple custom tabs for your own icons. (Pro)', link: '/pro-features' },
      { text: 'KMZ files supported as map overlays, and overlays are faster on long tracks.' },
      { text: 'Road Rally: Time Allowed and Average Speed for special stages.', link: '/how-to-create-a-road-rally' },
      { text: 'OpenRally: one danger level exported per waypoint (the highest).', link: '/timing-scoring' },
    ],
  },
];

// "2.6.7" or "2.5.2 – 2.5.3" (the last one counts) → [2, 6, 7]
function parts(version) {
  return String(version).split(/\s+/).pop().split('.').map(Number);
}

export function compareVersions(a, b) {
  const x = parts(a);
  const y = parts(b);
  for (let i = 0; i < 3; i++) if ((x[i] || 0) !== (y[i] || 0)) return (x[i] || 0) - (y[i] || 0);
  return 0;
}

// Versions in the app's published notes that are newer than the hand-edited
// list, newest first, as { version, releaseDate, sections: [{ title, items }] }.
// Dates are known for the current and the previous published release.
export function autoReleases() {
  const newest = RELEASES[0] ? RELEASES[0].version : '0.0.0';
  const dates = {
    [appVersion.version]: appVersion.releaseDate,
    [previousVersion.version]: previousVersion.releaseDate,
  };
  return (releaseNotes.releases || [])
    .filter((r) => compareVersions(r.version, newest) > 0)
    .filter((r) => compareVersions(r.version, appVersion.version) <= 0)
    .map((r) => ({ ...r, releaseDate: dates[r.version] || null }));
}

// Show the menu badge for this many days after the newest published release.
export const NEW_BADGE_DAYS = 30;

export function hasRecentRelease(now = Date.now()) {
  const published = Date.parse(appVersion.releaseDate);
  if (Number.isNaN(published)) return false;
  const days = (now - published) / 86400000;
  return days >= 0 && days <= NEW_BADGE_DAYS;
}
